import { useEffect, useRef } from 'react';
import { JobRanking } from '@doda/common/src/types/jobSearchList/jobContent';
import { RankedJobCard } from '@doda/pc/src/pages/DodaFront/View/JobSearchList/components/JobRanking/RankingContainer/RankingPanel/RankedJobCard';
import { RankingNote } from '@doda/pc/src/pages/DodaFront/View/JobSearchList/components/JobRanking/RankingContainer/RankingPanel/RankingNote';
import { JobRankingLink } from '@doda/pc/src/pages/DodaFront/View/JobSearchList/components/JobRanking/RankingContainer/RankingPanel/JobRankingLink';
import { measureRankingVisible } from '@doda/common/src/logics/jobSearchList/jobRankingAnalytics';
import { useIntersection } from '@doda/common/src/hooks/useIntersection';
import styles from './index.module.scss';

/**
 * 人気求人ランキングパネルProps
 * @property jobRanking 1検索軸単位のランキングデータ
 */
interface RankingPanelProps {
  jobRanking: JobRanking;
}

/**
 * 人気求人ランキングパネル
 * 1検索軸単位でのランキング、注意事項、人気求人ランキングページリンクを出力
 */
export const RankingPanel = ({ jobRanking }: RankingPanelProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { intersecting } = useIntersection(ref);

  // 初回表示で発火済みか
  const hasMeasuredInitialRef = useRef(false);
  // 前回の表示状態
  const prevVisibleRef = useRef(false);

  useEffect(() => {
    // 表示中か判定
    const visible = !!ref.current && ref.current.offsetParent !== null;

    // 初回表示
    const isInitialVisible =
      intersecting && visible && !hasMeasuredInitialRef.current;

    // タブ切替
    const isTabSwitch =
      visible && !prevVisibleRef.current && hasMeasuredInitialRef.current;

    if (!isInitialVisible && !isTabSwitch) {
      // 初回表示でもタブ切替でもない場合（再スクロール等）は計測しない
      prevVisibleRef.current = visible;
      return;
    }

    measureRankingVisible(
      jobRanking.searchAxis.queryKey,
      jobRanking.searchAxis.queryValue,
      jobRanking.jobs.map((job) => job.id)
    );

    hasMeasuredInitialRef.current = true;
    prevVisibleRef.current = visible;
  }, [intersecting, jobRanking]);

  return (
    <div ref={ref} className={styles.rankingPanel}>
      {jobRanking.jobs.map((job) => (
        <RankedJobCard key={job.id} rankedJob={job} />
      ))}
      <RankingNote updatedDate={jobRanking.updatedDate} />
      <div className={styles.rankingPanel__linkWrapper}>
        <JobRankingLink
          label={jobRanking.searchAxis.label}
          queryKey={jobRanking.searchAxis.queryKey}
          queryValue={jobRanking.searchAxis.queryValue}
        />
      </div>
    </div>
  );
};
